import { helpElement } from '../../../help/attrs';
import React, { useMemo } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import SearchableSelect from '../../common/SearchableSelect';

export interface RefOption {
    id: string;
    label: string;
}

interface RefAutocompleteProps {
    options: RefOption[];
    /** Selected ref id (not the full option object) -- matches the id-based `value` convention of
     * `CommercialPartyAutocomplete`/`OrgUnitPicker` so callers can persist a bare `*Id` field. */
    value: string | null;
    onChange: (id: string | null) => void;
    loading?: boolean;
    disabled?: boolean;
    label?: string;
    helperText?: string;
    sx?: SxProps<Theme>;
    /** Help-mode key; lands on the picker's root element. */
    'data-help'?: string;
}

/**
 * Thin MUI Autocomplete over a caller-supplied `{ id, label }[]` list, for optional soft-reference
 * pickers (packing list / bill of lading -> EXP form, buyer order, export LC) that today are raw
 * "…(optional UUID)" text fields. Modelled on `CommercialPartyAutocomplete` (same module) and
 * `OrgUnitPicker` (client/src/components/common/OrgUnitPicker.tsx) -- same conventions, id-based
 * `value`/`onChange`.
 *
 * A `value` whose id isn't present in `options` (e.g. it points at a ref that's since been
 * archived, or the caller's fetched page just didn't include it) is preserved by injecting a
 * synthetic `{ id: value, label: value }` entry -- the `OrgUnitPicker.selectedOption` pattern --
 * rather than silently dropping the selection: otherwise re-saving an edited record without
 * touching this field would clear the link.
 */
const RefAutocomplete: React.FC<RefAutocompleteProps> = ({
    options, value, onChange, loading, disabled, label, helperText, sx, 'data-help': dataHelp,
}) => {
    // A `value` missing from `options` is kept selectable via a synthetic entry.
    const selectedOption = useMemo(
        () => (value ? options.find(o => o.id === value) ?? { id: value, label: value } : null),
        [options, value]
    );

    return (
        <SearchableSelect<RefOption>
            options={options}
            value={value}
            selectedOption={selectedOption}
            onChange={(id) => onChange(id || null)}
            getOptionLabel={option => option.label}
            loading={loading}
            disabled={disabled}
            sx={sx}
            label={label}
            helperText={helperText}
            {...helpElement(dataHelp)}
            noOptionsText={loading ? 'Loading…' : 'No options'}
        />
    );
};

export default RefAutocomplete;
